import { useMemo } from 'react';
import type { Contest } from '@/mocks/adminContests';
import { contestTypeLabels, contestTypeIcons, contestTypeColors } from '@/mocks/adminContests';

interface HistoryPanelProps {
  contests: Contest[];
  onViewWinners: (contest: Contest) => void;
  onPublishWinner: (contest: Contest) => void;
}

export default function HistoryPanel({ contests, onViewWinners, onPublishWinner }: HistoryPanelProps) {
  const finishedContests = useMemo(() => {
    return contests
      .filter((c) => c.status === 'finalizado')
      .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
  }, [contests]);

  const conGanador = finishedContests.filter((c) => c.winnerName);
  const sinGanador = finishedContests.filter((c) => !c.winnerName);

  const totalPremios = conGanador.length;
  const totalParticipantesHistorico = finishedContests.reduce((s, c) => s + c.participants, 0);
  const totalSharesHistorico = finishedContests.reduce((s, c) => s + c.shares, 0);

  if (finishedContests.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-history-line text-3xl text-foreground-400"></i>
        </div>
        <h3 className="text-foreground-950 font-bold text-base mb-1">Sin historial todavía</h3>
        <p className="text-foreground-500 text-sm max-w-sm mx-auto">
          Cuando finalices concursos y elijas ganadores, aparecerán acá con todo su historial.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 rounded-xl border border-background-200 p-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
              <i className="ri-check-double-line text-foreground-500 text-sm"></i>
            </div>
          </div>
          <p className="text-foreground-950 text-lg font-bold">{finishedContests.length}</p>
          <p className="text-foreground-400 text-[11px]">Finalizados</p>
        </div>

        <div className="bg-background-50 rounded-xl border border-background-200 p-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-trophy-line text-amber-600 text-sm"></i>
            </div>
          </div>
          <p className="text-foreground-950 text-lg font-bold">{totalPremios}</p>
          <p className="text-foreground-400 text-[11px]">Con ganador</p>
        </div>

        <div className="bg-background-50 rounded-xl border border-background-200 p-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
              <i className="ri-group-line text-primary-600 text-sm"></i>
            </div>
          </div>
          <p className="text-foreground-950 text-lg font-bold">{totalParticipantesHistorico.toLocaleString()}</p>
          <p className="text-foreground-400 text-[11px]">Participantes</p>
        </div>

        <div className="bg-background-50 rounded-xl border border-background-200 p-3">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
              <i className="ri-share-forward-line text-sky-600 text-sm"></i>
            </div>
          </div>
          <p className="text-foreground-950 text-lg font-bold">{totalSharesHistorico.toLocaleString()}</p>
          <p className="text-foreground-400 text-[11px]">Compartidos</p>
        </div>
      </div>

      {/* Pendientes (sin ganador) */}
      {sinGanador.length > 0 && (
        <div>
          <h4 className="text-foreground-700 text-xs font-semibold mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-orange-400"></span>
            Pendientes de ganador ({sinGanador.length})
          </h4>
          <div className="space-y-3">
            {sinGanador.map((contest) => {
              const typeStyle = contestTypeColors[contest.type];
              return (
                <div key={contest.id} className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/80 transition-all group">
                  <div className={`w-1.5 h-12 rounded-full ${typeStyle.bar} shrink-0`}></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${typeStyle.bg} ${typeStyle.text}`}>
                        <i className={`${contestTypeIcons[contest.type]} text-[8px]`}></i>
                        {contestTypeLabels[contest.type]}
                      </span>
                      <span className="text-foreground-400 text-[10px]">
                        {new Date(contest.endDate).toLocaleDateString('es-MX', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <h5 className="text-foreground-950 font-bold text-sm">{contest.title}</h5>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-foreground-400 text-[10px]">{contest.participants.toLocaleString()} participantes</span>
                      <span className="text-foreground-400 text-[10px]">{contest.shares.toLocaleString()} compartidos</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-orange-600 text-[10px] font-bold flex items-center gap-1">
                      <i className="ri-alert-line text-[10px]"></i>
                      Sin ganador
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Con ganador */}
      {conGanador.length > 0 && (
        <div>
          <h4 className="text-foreground-700 text-xs font-semibold mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            Ganadores ({conGanador.length})
          </h4>
          <div className="space-y-3">
            {conGanador.map((contest) => {
              const typeStyle = contestTypeColors[contest.type];
              return (
                <div key={contest.id} className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/80 transition-all group">
                  <div className={`w-1.5 h-12 rounded-full ${typeStyle.bar} shrink-0`}></div>

                  {/* Winner avatar */}
                  {contest.winnerAvatar && (
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-amber-300 shrink-0 bg-amber-100">
                      <img src={contest.winnerAvatar} alt={contest.winnerName || ''} className="w-full h-full object-cover" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${typeStyle.bg} ${typeStyle.text}`}>
                        <i className={`${contestTypeIcons[contest.type]} text-[8px]`}></i>
                        {contestTypeLabels[contest.type]}
                      </span>
                      <span className="text-foreground-400 text-[10px]">
                        {new Date(contest.endDate).toLocaleDateString('es-MX', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <h5 className="text-foreground-950 font-bold text-sm">{contest.title}</h5>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-foreground-400 text-[10px]">{contest.participants.toLocaleString()} participantes</span>
                      <span className="text-foreground-400 text-[10px]">{contest.shares.toLocaleString()} compartidos</span>
                      <span className="inline-flex items-center gap-1 text-amber-700 text-[10px] font-bold">
                        <i className="ri-trophy-fill text-[10px]"></i>
                        {contest.winnerName}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {contest.winnerPublished ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[9px] font-bold">
                        <i className="ri-check-double-line text-[9px]"></i>
                        Publicado
                      </span>
                    ) : (
                      <button
                        onClick={() => onPublishWinner(contest)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-[10px] font-semibold cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap border border-amber-200"
                      >
                        <i className="ri-megaphone-line text-[10px]"></i>
                        Publicar
                      </button>
                    )}
                    <button
                      onClick={() => onViewWinners(contest)}
                      className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                      title="Ver ganador"
                    >
                      <i className="ri-eye-line text-foreground-500 text-sm"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Type distribution */}
      <div className="p-4 rounded-xl bg-background-50 border border-background-200/70">
        <h4 className="text-foreground-700 text-xs font-semibold mb-3">Distribución por tipo</h4>
        <div className="grid grid-cols-5 gap-2">
          {(['sorteo', 'trivia', 'comentarios', 'compartir', 'participacion'] as const).map((t) => {
            const count = finishedContests.filter((c) => c.type === t).length;
            const colors = contestTypeColors[t];
            return (
              <div key={t} className="text-center p-2 rounded-lg bg-background-100">
                <div className={`w-8 h-8 mx-auto mb-1 rounded-lg ${colors.bg} flex items-center justify-center`}>
                  <i className={`${contestTypeIcons[t]} ${colors.text} text-sm`}></i>
                </div>
                <p className="text-foreground-950 font-bold text-sm">{count}</p>
                <p className="text-foreground-400 text-[9px]">{contestTypeLabels[t]}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}